import React, { useMemo, useState } from "react";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  CopyIcon,
  EyeIcon,
  FilterIcon,
  InfoIcon,
  SearchIcon,
  SyncIcon,
  UsersIcon,
  XCircleIcon,
  XIcon,
} from "../../Components/Icons";
import {
  COMPARE_TEMPLATE,
  DUPLICATE_FILTERS,
  DUPLICATE_GROUPS,
  DUPLICATE_STATS,
  DUPLICATE_TABS,
  TOTAL_GROUPS,
} from "./duplicates";
import "./DataQuality.css";

const ICONS = {
  copy: CopyIcon,
  alert: AlertTriangleIcon,
  users: UsersIcon,
  check: CheckCircleIcon,
  reject: XCircleIcon,
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FILTERS = DUPLICATE_FILTERS.reduce(
  (acc, filter) => ({ ...acc, [filter.key]: "" }),
  {}
);

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/\s+/g, "-")}`;

/** Substitutes the selected group's identity into the comparison template. */
const compareFor = (group) => ({
  ...COMPARE_TEMPLATE,
  fields: COMPARE_TEMPLATE.fields.map((field) => {
    if (field.label === "NIF") {
      return {
        ...field,
        values: { dgi: group.nif, vat: group.nif, br: group.nif },
      };
    }
    if (field.label === "Legal Name") {
      return {
        ...field,
        values: {
          dgi: group.name,
          vat: group.name.replace("Limited", "Ltd."),
          br: group.name,
        },
      };
    }
    return field;
  }),
  recommendation: `Keep DGI Register record as primary and merge the other ${
    group.records - 1
  } records.`,
});

export default function DuplicateRecords() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [openGroup, setOpenGroup] = useState(DUPLICATE_GROUPS[0].nif);

  const setFilter = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setPage(1);
  };

  const filtersActive =
    search.trim() !== "" || Object.values(filters).some(Boolean);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return DUPLICATE_GROUPS.filter((group) => {
      if (activeTab !== "all" && group.status !== activeTab) return false;
      if (term && !`${group.name} ${group.nif}`.toLowerCase().includes(term)) {
        return false;
      }
      if (filters.match && group.match !== filters.match) return false;
      if (filters.status && group.status !== filters.status) return false;
      if (filters.source) {
        const short = { "DGI Register": "DGI", "VAT System": "VAT", "Business Registry": "BR", Customs: "Customs" };
        if (!group.sources.includes(short[filters.source])) return false;
      }
      return true;
    });
  }, [activeTab, search, filters]);

  const totalPages = Math.ceil(TOTAL_GROUPS / perPage);
  const group = rows.find((item) => item.nif === openGroup) ?? null;
  const compare = group ? compareFor(group) : null;

  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggleAll = () =>
    setSelected(allSelected ? [] : rows.map((row) => row.nif));

  const toggleRow = (nif) =>
    setSelected((prev) =>
      prev.includes(nif) ? prev.filter((item) => item !== nif) : [...prev, nif]
    );

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Duplicate Resolution</h1>
          <p className="page__subtitle">
            Review and resolve duplicate taxpayer records to ensure a single,
            trusted view.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <CalendarIcon size={17} />
            01 Jan 2026 - 01 Sep 2026
            <ChevronDownIcon size={16} />
          </button>
          <button type="button" className="link page__refresh">
            <SyncIcon size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Duplicate summary">
        {DUPLICATE_STATS.map((card) => {
          const Icon = ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="dq-card h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="dq-card__body">
                  <p className="kpi__label">{card.label}</p>
                  <p className="dq-card__value">{card.value}</p>
                  <p className="kpi__foot">{card.foot}</p>
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {/* ------------------------------------------- list + compare pane -- */}
      <div className={`dup-layout${compare ? " dup-layout--split" : ""}`}>
        <section className="dgi-card">
          <div className="tabs tabs--scroll">
            {DUPLICATE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`tabs__tab${
                  activeTab === tab.key ? " tabs__tab--active" : ""
                }`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setPage(1);
                }}
              >
                {tab.label} ({tab.count.toLocaleString("en-US")})
              </button>
            ))}
          </div>

          <div className="dup-filters">
            <div className="filters__search">
              <input
                type="search"
                placeholder="Search by name, NIF or reference..."
                aria-label="Search duplicate groups"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <SearchIcon size={17} />
            </div>

            {DUPLICATE_FILTERS.map((filter) => (
              <span className="select select--block" key={filter.key}>
                <select
                  value={filters[filter.key]}
                  aria-label={filter.placeholder}
                  onChange={setFilter(filter.key)}
                >
                  <option value="">{filter.placeholder}</option>
                  {filter.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            ))}

            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <FilterIcon size={16} />
              Filters
            </button>
            <button
              type="button"
              className="filters__clear"
              onClick={clearFilters}
              disabled={!filtersActive}
            >
              Clear Filters
            </button>
          </div>

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--dup">
              <thead>
                <tr>
                  <th className="dgi-table__check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all groups"
                    />
                  </th>
                  <th>
                    <span className="dgi-card__title dup-th">
                      Duplicate Groups
                      <span className="dgi-card__info" title="About groups">
                        <InfoIcon size={14} />
                      </span>
                    </span>
                  </th>
                  <th>Match Level</th>
                  <th>Potential Duplicates</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr
                    key={item.nif}
                    className={item.nif === openGroup ? "is-open" : undefined}
                  >
                    <td className="dgi-table__check">
                      <input
                        type="checkbox"
                        checked={selected.includes(item.nif)}
                        onChange={() => toggleRow(item.nif)}
                        aria-label={`Select ${item.name}`}
                      />
                    </td>
                    <td>
                      <span className="dup-group">
                        <span className="dup-group__text">
                          <button
                            type="button"
                            className="link"
                            onClick={() => setOpenGroup(item.nif)}
                          >
                            NIF: {item.nif}
                          </button>
                          <span className="dgi-table__sub">{item.name}</span>
                        </span>
                        <button
                          type="button"
                          className="dup-group__toggle"
                          aria-label={`Expand ${item.name}`}
                        >
                          <ChevronDownIcon size={16} />
                        </button>
                      </span>
                    </td>
                    <td>
                      <span className={chipClass(item.match)}>{item.match}</span>
                      <span className="dgi-table__sub">{item.score}% match</span>
                    </td>
                    <td>
                      {item.records} records
                      <span className="dgi-table__sub">{item.sources}</span>
                    </td>
                    <td>
                      <span className={chipClass(item.status)}>
                        {item.status}
                      </span>
                    </td>
                    <td>
                      {item.updatedDate}
                      <span className="dgi-table__sub">{item.updatedTime}</span>
                    </td>
                    <td>
                      <span className="row-actions">
                        <button
                          type="button"
                          className="row-actions__btn"
                          aria-label={`Preview ${item.name}`}
                        >
                          <EyeIcon size={17} />
                        </button>
                        <button
                          type="button"
                          className="row-actions__btn"
                          aria-label={`Compare ${item.name}`}
                          onClick={() => setOpenGroup(item.nif)}
                        >
                          <ArrowRightIcon size={17} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">
              No duplicate groups match these filters.{" "}
              <button type="button" className="link" onClick={clearFilters}>
                Clear filters
              </button>
            </p>
          )}

          <div className="listbar listbar--foot">
            <p className="listbar__count">
              Showing {rows.length === 0 ? 0 : 1} to {rows.length} of{" "}
              {TOTAL_GROUPS.toLocaleString("en-US")} duplicate groups
            </p>

            <div className="listbar__pager">
              <span className="select">
                <select
                  value={perPage}
                  aria-label="Rows per page"
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} per page
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>

              <nav className="pager" aria-label="Pagination">
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="First page"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeftIcon size={16} />
                </button>
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Previous page"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                  <ChevronLeftIcon size={16} />
                </button>

                {[1, 2, 3, 4, 5].map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    className={`pager__btn${
                      entry === page ? " pager__btn--active" : ""
                    }`}
                    aria-current={entry === page ? "page" : undefined}
                    onClick={() => setPage(entry)}
                  >
                    {entry}
                  </button>
                ))}
                <span className="pager__gap">…</span>
                <button
                  type="button"
                  className="pager__btn"
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages.toLocaleString("en-US")}
                </button>

                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Next page"
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                >
                  <ChevronRightIcon size={16} />
                </button>
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Last page"
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  <ChevronsRightIcon size={16} />
                </button>
              </nav>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------ compare pane -- */}
        {compare && (
          <aside className="dgi-card compare">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">
                Compare Duplicate Records
                <span className="dgi-card__info" title="About comparison">
                  <InfoIcon size={15} />
                </span>
              </h2>
              <button
                type="button"
                className="dgi-card__more"
                aria-label="Close comparison"
                onClick={() => setOpenGroup(null)}
              >
                <XIcon size={18} />
              </button>
            </div>

            <div className="compare__score">
              <span className={chipClass(`${group.match} Match`)}>
                {group.match} Match
              </span>
              <span className="compare__score-text">
                {group.score}% match score
              </span>
            </div>

            <p className="compare__identity">
              NIF: {group.nif} <span>|</span> {group.name}
            </p>
            <p className="compare__found">
              {group.records} potential duplicate records found
            </p>

            <h3 className="compare__heading">Records</h3>
            <ul className="compare__legend">
              {compare.columns.map((column) => (
                <li key={column.key}>
                  <span className={`compare__dot compare__dot--${column.tone}`} />
                  {column.role}
                  {column.tone === "primary" && " (Recommended)"}
                </li>
              ))}
            </ul>

            <div className="dgi-table-wrap">
              <table className="dgi-table compare__table">
                <thead>
                  <tr>
                    <th>Field</th>
                    {compare.columns.map((column) => (
                      <th key={column.key}>
                        {column.label}
                        <span
                          className={`chip compare__role compare__role--${column.tone}`}
                        >
                          {column.role}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compare.fields.map((field) => (
                    <tr key={field.label}>
                      <td className="compare__field">{field.label}</td>
                      {compare.columns.map((column) => (
                        <td key={column.key}>{field.values[column.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="compare__action">
              <p className="compare__action-title">
                <span className="compare__dot compare__dot--primary" />
                Recommended Action
              </p>
              <p className="compare__action-text">
                <CheckCircleIcon size={17} />
                {compare.recommendation}
              </p>
            </div>

            <div className="compare__buttons">
              <button type="button" className="dgi-btn dgi-btn--merge">
                Merge Records
              </button>
              <button type="button" className="dgi-btn dgi-btn--outline compare__btn">
                Manual Review
              </button>
              <button type="button" className="dgi-btn dgi-btn--danger">
                Reject All
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
